import React from 'react';
import { 
  Search, Menu, X, ArrowRight, ChevronRight, Star, 
  BarChart2, Heart, ExternalLink, Plus, RefreshCw, 
  Download, Settings, Sun, Moon, Grid, Users, Shield, 
  Zap, Calendar, Globe, Home
} from 'lucide-react';

/**
 * Компонент Icon - универсальный компонент для отображения иконок
 * 
 * @param {string} name - Имя иконки
 * @param {string} color - Цветовая схема (indigo, purple, violet, fuchsia, cyan, emerald, orange, gray)
 * @param {string} size - Размер иконки (sm, md, lg, xl)
 * @param {string} className - Дополнительные классы CSS
 */
const Icon = ({ name, color = "indigo", size = "md", className = "" }) => {
  const colorMap = {
    indigo: "text-indigo-500 dark:text-indigo-400",
    purple: "text-purple-500 dark:text-purple-400", 
    violet: "text-violet-500 dark:text-violet-400",
    fuchsia: "text-fuchsia-500 dark:text-fuchsia-400",
    cyan: "text-cyan-500 dark:text-cyan-400",
    emerald: "text-emerald-500 dark:text-emerald-400",
    orange: "text-orange-500 dark:text-orange-400",
    gray: "text-gray-500 dark:text-gray-400",
  };
  
  const sizeMap = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
    xl: "w-8 h-8"
  };
  
  // Lucide иконки
  const LucideIcons = {
    search: <Search />,
    menu: <Menu />,
    close: <X />,
    arrowRight: <ArrowRight />,
    chevronRight: <ChevronRight />,
    star: <Star />,
    barChart: <BarChart2 />,
    heart: <Heart />,
    externalLink: <ExternalLink />,
    plus: <Plus />,
    refreshCw: <RefreshCw />,
    download: <Download />,
    settings: <Settings />,
    sun: <Sun />,
    moon: <Moon />,
    grid: <Grid />,
    users: <Users />,
    shield: <Shield />,
    zap: <Zap />,
    calendar: <Calendar />,
    globe: <Globe />,
    home: <Home />,
  };
  
  // Кастомные SVG иконки
  const CustomIcons = {
    calculator: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="4" y="3" width="16" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <rect x="7" y="8" width="3" height="3" rx="0.5" fill="currentColor" />
        <rect x="14" y="8" width="3" height="3" rx="0.5" fill="currentColor" />
        <rect x="7" y="14" width="3" height="3" rx="0.5" fill="currentColor" />
        <rect x="14" y="14" width="3" height="3" rx="0.5" fill="currentColor" />
      </svg>
    ),
    chart: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 12H7V20H3V12Z" stroke="currentColor" fill="currentColor" fillOpacity="0.2" strokeWidth="1.5" />
        <path d="M10 8H14V20H10V8Z" stroke="currentColor" fill="currentColor" fillOpacity="0.2" strokeWidth="1.5" />
        <path d="M17 4H21V20H17V4Z" stroke="currentColor" fill="currentColor" fillOpacity="0.2" strokeWidth="1.5" />
      </svg>
    ),
    currency: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1" />
        <path d="M12 7V17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M15 10H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M15 14H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    coffee: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M17 2V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M12 2V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M7 2V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M19 10H5C3.89543 10 3 9.10457 3 8V7C3 5.89543 3.89543 5 5 5H19C20.1046 5 21 5.89543 21 7V8C21 9.10457 20.1046 10 19 10Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M17.5 15H6.5C5.11929 15 4 13.8807 4 12.5V10H20V12.5C20 13.8807 18.8807 15 17.5 15Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M13.5 19C13.5 20.1046 12.6046 21 11.5 21H11C9.89543 21 9 20.1046 9 19V15H13.5V19Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
      </svg>
    ),
    key: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="9" cy="15" r="6" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1" />
        <circle cx="9" cy="15" r="2" stroke="currentColor" strokeWidth="1.5" />
        <path d="M14 10L21 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M18 6L16 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M15 9L17 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    crown: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 20H21V17L17 7L12 14L7 7L3 17V20Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4 20L20 20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    printer3d: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19 5H5C3.89543 5 3 5.89543 3 7V10C3 11.1046 3.89543 12 5 12H7V19H17V12H19C20.1046 12 21 11.1046 21 10V7C21 5.89543 20.1046 5 19 5Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M12 7V19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M7 15H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M7 12H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <rect x="9" y="3" width="6" height="2" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
      </svg>
    ),
    megaphone: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 9H7L15 3V21L7 15H3V9Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <circle cx="17" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M19.5 12H21M2 12H3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    tractor: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="12" width="16" height="6" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <circle cx="18" cy="18" r="3" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <circle cx="6" cy="18" r="2" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <rect x="10" y="6" width="6" height="6" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M10 12V9H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    brain: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 3C8 3 5 6 5 10C5 14 8 17 12 17C16 17 19 14 19 10C19 6 16 3 12 3Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M8 10H16M8 13H16M12 7V17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M4 12H5M19 12H20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    scale: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 3V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M4 8H20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M7 8L5 15H10L8 8" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M17 8L15 15H20L18 8" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
      </svg>
    ),
    
    compass: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M12 12L16 8L8 14L14 16L12 12Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.2"/>
      </svg>
    ),
    
    smartphone: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="6" y="2" width="12" height="20" rx="2" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <rect x="11" y="18" width="2" height="1" rx="0.5" fill="currentColor"/>
        <rect x="9" y="4" width="6" height="1" rx="0.5" fill="currentColor"/>
      </svg>
    ),
    
    car: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="11" width="18" height="6" rx="2" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M5 11L6 7H18L19 11" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <circle cx="7" cy="18" r="2" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <circle cx="17" cy="18" r="2" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
      </svg>
    ),
    
    alertTriangle: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L22 20H2L12 2Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M12 8V13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="12" cy="17" r="1" stroke="currentColor" strokeWidth="1.5" fill="currentColor"/>
      </svg>
    ),
    
    database: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="12" cy="5" rx="8" ry="3" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M4 5V19C4 20.1046 4.89543 21 6 21H18C19.1046 21 20 20.1046 20 19V5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <ellipse cx="12" cy="12" rx="8" ry="3" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <ellipse cx="12" cy="19" rx="8" ry="3" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
      </svg>
    ),
    
    refreshCw: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M21 12A9 9 0 113 12" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <path d="M3 3V11H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    
    building2: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="4" y="3" width="6" height="18" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <rect x="14" y="8" width="6" height="13" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M10 21H14V3" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M6 11H8M6 15H8M14 11H16M14 15H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    
    trendingUp: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 17L9 11L13 15L21 7" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
        <path d="M21 7V13H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    
    piggyBank: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 11C3 8 5 6 8 6H16C19 6 21 8 21 11C21 14 19 16 16 16H8C5 16 3 14 3 11Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <circle cx="11" cy="11" r="1" stroke="currentColor" strokeWidth="1.5" fill="currentColor"/>
        <path d="M16 18C16 19.1046 15.1046 20 14 20C12.8954 20 12 19.1046 12 18" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
    
    coins: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="12" cy="7" rx="8" ry="3" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M4 7V17C4 18.1046 4.89543 19 6 19H18C19.1046 19 20 18.1046 20 17V7" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <ellipse cx="12" cy="17" rx="8" ry="3" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
      </svg>
    ),
    
    graduationCap: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2 9L12 2L22 9L12 16L2 9Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M12 16V22M8 18H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    
    receipt: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 2H20V22L16 18L12 22L8 18L4 22V2Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M8 6H16M8 10H16M8 14H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    
    shoppingBag: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 2H18L20 6V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V6L6 2Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M16 10V6C16 5 15 4 14 4H10C9 4 8 5 8 6V10" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
    
    percent: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="6" cy="6" r="2" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <circle cx="18" cy="18" r="2" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M19 5L5 19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),

    refresh: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M21 12A9 9 0 113 12" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <path d="M3 3V11H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    
    wallet: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="6" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M2 10H22" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="18" cy="13" r="1.5" stroke="currentColor" strokeWidth="1.5" fill="currentColor"/>
      </svg>
    ),
    
    activity: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 12H8L11 7L14 17L17 12H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      </svg>
    ),
    
    apple: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M17.2 9C16.8 7.7 17.3 6.3 18.3 5.4C17.1 4 15 3.7 13.8 4.8C12.7 5.8 12.2 7.8 13 9.2C14 11 16.2 11.2 17.2 9Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M8 21C12 21 14 18 14 18C14 18 12 15 8 15C4 15 2 18 2 18C2 18 4 21 8 21Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
      </svg>
    ),
    
    activitySquare: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="2" width="20" height="20" rx="2" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M16 8L11 16L8 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      </svg>
    ),
    
    droplet: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2C8 6 5 9 5 12C5 16 8 19 12 19C16 19 19 16 19 12C19 9 16 6 12 2Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M12 7V13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    wallpaper: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="4" width="20" height="16" fill="currentColor" fillOpacity="0.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <line x1="2" y1="8" x2="22" y2="8" stroke="currentColor" strokeWidth="1.5"/>
        <line x1="6" y1="8" x2="6" y2="20" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
    
    ruler: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="6" width="20" height="12" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M6 6V18M10 6V18M14 6V18M18 6V18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    
    paintBucket: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2 20L12 10L20 18V22H2V20Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M12 10L14 8L16 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="14" cy="16" r="2" fill="currentColor" fillOpacity="0.1" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
    
    lightbulb: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 18H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M12 2C8 2 5 5 5 9C5 12.5 8 15 12 15C16 15 19 12.5 19 9C19 5 16 2 12 2Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <line x1="12" y1="15" x2="12" y2="18" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
    
    squareIcon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="3" width="18" height="18" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M3 3L21 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    
    thermometer: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 14.76V5A2 2 0 0 0 10 5V14.76A4 4 0 1 0 14 14.76Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <circle cx="12" cy="18" r="2" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
      </svg>
    ),
    
    calendarCheck: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M16 2V6M8 2V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M9 13L11 15L15 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    
    calendarDays: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <line x1="3" y1="9" x2="21" y2="9" stroke="currentColor" strokeWidth="1.5"/>
        <line x1="9" y1="3" x2="9" y2="21" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
    
    clock: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M12 7V12L15 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    
    timer: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 2H18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="12" cy="14" r="8" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M12 10V14L14 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),

    image: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M3 17L9 11L15 17L18 14L21 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    
    type: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 4H20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M12 4V20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M7 20H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    
    dice: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <circle cx="8" cy="8" r="1" fill="currentColor"/>
        <circle cx="16" cy="8" r="1" fill="currentColor"/>
        <circle cx="12" cy="12" r="1" fill="currentColor"/>
        <circle cx="8" cy="16" r="1" fill="currentColor"/>
        <circle cx="16" cy="16" r="1" fill="currentColor"/>
      </svg>
    ),
    
    mail: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M3 5L12 13L21 5" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
    
    briefcase: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="7" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M9 7V5C9 4.44772 9.44772 4 10 4H14C14.5523 4 15 4.44772 15 5V7" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M3 13H21" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
    
    mapPin: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 21C12 21 19 14.5 19 9C19 5.68629 16.3137 3 13 3C9.68629 3 7 5.68629 7 9C7 14.5 12 21 12 21Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <circle cx="12" cy="9" r="2" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
    
    plane: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2 16L22 12L2 8L6 12L2 16Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <path d="M6 12L12 21V15L6 12Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
      </svg>
    ),
    
    piggyBank: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 11V10C5 7 7.5 5 11 5H13C16.5 5 19 7 19 10V14C19 15.5 18.5 16.5 17.5 17H16V19H8V17H6.5C5.5 16.5 5 15.5 5 14V13" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
        <circle cx="16" cy="10" r="1" fill="currentColor"/>
        <path d="M11 3H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  };
  
  // Выбор иконки с приоритетом для кастомных
  const selectedIcon = CustomIcons[name] || LucideIcons[name];
  
  if (!selectedIcon) {
    console.warn(`Icon "${name}" not found`);
    return <div className={`${colorMap[color]} ${sizeMap[size]} ${className}`}>{name}</div>;
  }
  
  // Обертка для добавления цвета и размера к иконке
  return (
    <div className={`${colorMap[color]} ${sizeMap[size]} ${className}`}>
      {selectedIcon}
    </div>
  );
};

export default Icon;